// Regras puras da página "Segmentos × 4 filtros" (cabeça de praia, Semana 3 da Bcorp).
// Sem React e sem antd, para rodar no jest do CRA. O servidor (handlers/beachheadRegras.js) faz a
// conta de verdade; aqui só se decide o que MOSTRAR e o que MANDAR para gravar.
//
// ACEITE vem dos dados e nunca é editado. CAPACIDADE, POSICIONAMENTO e TIMING são nota 1 a 3 + motivo,
// dados por uma pessoa. Soma = aceite + as 3 notas, só quando as 3 existem.

export type FiltroPessoa = 'capacidade' | 'posicionamento' | 'timing';
export const FILTROS: FiltroPessoa[] = ['capacidade', 'posicionamento', 'timing'];

export const ROTULO_FILTRO: Record<FiltroPessoa | 'aceite', string> = {
  aceite: 'Aceite', capacidade: 'Capacidade', posicionamento: 'Posicionamento', timing: 'Timing',
};

export type Nota = 1 | 2 | 3;
export interface NotaMotivo { nota: Nota | null; motivo: string | null }
export type SugestaoIa = Partial<Record<FiltroPessoa, NotaMotivo>>;

export interface LinhaBeachhead {
  chave: string; segmento: string;
  casos: number; ganhos: number; perdidos: number; taxa: number;
  aceite: { nota: number; motivo: string };
  /** "Na mesa": desligado tira o segmento da disputa. */
  candidato: boolean;
  /** Revisão gravada; nula = linha ainda não gravada. */
  revisao: number | null;
  atualizado_em: string | null; atualizado_por: string | null;
  capacidade: NotaMotivo; posicionamento: NotaMotivo; timing: NotaMotivo;
  sugestao_ia: SugestaoIa | null;
  completa: boolean; soma: number | null; soma_parcial: number;
}

/** Campos no formato do servidor. Na edição da tela, só entram os que a pessoa mexeu. */
export interface CamposBeachhead {
  candidato?: boolean;
  capacidade_nota?: number | null; capacidade_motivo?: string | null;
  posicionamento_nota?: number | null; posicionamento_motivo?: string | null;
  timing_nota?: number | null; timing_motivo?: string | null;
}

const num = (v: unknown, padrao = 0): number => {
  const n = Number(v);
  return v == null || v === '' || !Number.isFinite(n) ? padrao : n;
};
const ehNota = (v: unknown): v is Nota => v === 1 || v === 2 || v === 3;
const notaOuNula = (v: unknown): Nota | null => {
  const n = v == null || v === '' ? NaN : Number(v);
  return ehNota(n) ? n : null;
};
const textoOuNulo = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
/** Motivo como vai para o servidor: sem espaço nas pontas; vazio vira nulo. */
const motivoLimpo = (v: unknown): string | null => (typeof v === 'string' ? v.trim() || null : null);

function notaMotivo(x: any): NotaMotivo {
  return { nota: notaOuNula(x?.nota), motivo: textoOuNulo(x?.motivo) };
}

/** Notas efetivas (gravado, trocado pelo que está em edição), na ordem de FILTROS. */
function notasEfetivas(l: LinhaBeachhead, edicao?: CamposBeachhead): (Nota | null)[] {
  const e = edicao || {};
  return FILTROS.map((f) => {
    const k = `${f}_nota` as const;
    return k in e ? notaOuNula(e[k]) : l[f].nota;
  });
}

/**
 * Soma com os valores EFETIVOS: o que está gravado, trocado pelo que está em edição.
 * `soma` só existe com as 3 notas; `parcial` soma o que já tem (aceite + notas dadas).
 */
export function somaDe(l: LinhaBeachhead, edicao?: CamposBeachhead): { soma: number | null; parcial: number; completa: boolean } {
  const notas = notasEfetivas(l, edicao);
  const parcial = num(l.aceite?.nota) + notas.reduce<number>((a, n) => a + (n || 0), 0);
  const completa = notas.every((n) => n != null);
  return { soma: completa ? parcial : null, parcial, completa };
}

/** Linha como o SERVIDOR manda -> linha como a tela usa (defensivo: número vira number, vazio vira nulo). */
export function normalizarLinha(r: any): LinhaBeachhead {
  const sug = r?.sugestao_ia && typeof r.sugestao_ia === 'object' ? r.sugestao_ia : null;
  let sugestao: SugestaoIa | null = null;
  if (sug) {
    sugestao = {};
    for (const f of FILTROS) if (sug[f] && typeof sug[f] === 'object') sugestao[f] = notaMotivo(sug[f]);
  }
  const segmento = String(r?.segmento ?? r?.chave ?? '');
  const base: LinhaBeachhead = {
    chave: String(r?.chave ?? segmento), segmento,
    casos: num(r?.casos), ganhos: num(r?.ganhos), perdidos: num(r?.perdidos), taxa: num(r?.taxa),
    aceite: { nota: num(r?.aceite?.nota), motivo: String(r?.aceite?.motivo ?? '') },
    candidato: r?.candidato !== false,
    revisao: r?.revisao == null ? null : num(r.revisao),
    atualizado_em: textoOuNulo(r?.atualizado_em), atualizado_por: textoOuNulo(r?.atualizado_por),
    capacidade: notaMotivo(r?.capacidade), posicionamento: notaMotivo(r?.posicionamento), timing: notaMotivo(r?.timing),
    sugestao_ia: sugestao, completa: false, soma: null, soma_parcial: 0,
  };
  const s = somaDe(base);
  return { ...base, completa: s.completa, soma: s.soma, soma_parcial: s.parcial };
}

/**
 * Mesma ordem do servidor: na mesa primeiro; soma cheia antes da parcial; soma maior primeiro;
 * depois soma parcial, taxa e número de casos (maiores primeiro) e o nome. A lista de entrada não muda.
 */
export function ordenarLinhas(linhas: LinhaBeachhead[]): LinhaBeachhead[] {
  return [...linhas].sort((a, b) => {
    if (a.candidato !== b.candidato) return a.candidato ? -1 : 1;
    if ((a.soma != null) !== (b.soma != null)) return a.soma != null ? -1 : 1;
    if (a.soma != null && b.soma != null && a.soma !== b.soma) return b.soma - a.soma;
    if (a.soma_parcial !== b.soma_parcial) return b.soma_parcial - a.soma_parcial;
    if (a.taxa !== b.taxa) return b.taxa - a.taxa;
    if (a.casos !== b.casos) return b.casos - a.casos;
    return a.segmento.localeCompare(b.segmento, 'pt-BR');
  });
}

/** Os dois primeiros da mesa com as 4 notas (os que a Bcorp manda discutir). Pode vir com 0, 1 ou 2 nomes. */
export function doisPrimeiros(linhas: LinhaBeachhead[]): string[] {
  return ordenarLinhas(linhas).filter((l) => l.candidato && l.soma != null).slice(0, 2).map((l) => l.segmento);
}

/**
 * Só o que a pessoa mudou, no formato do servidor. Motivo vazio vira nulo; nota 0 (o "limpar" da
 * estrela) vira nula. `edicao` pode trazer campos iguais ao gravado: esses ficam de fora.
 */
export function camposAlterados(base: LinhaBeachhead, edicao: CamposBeachhead): CamposBeachhead {
  const saida: CamposBeachhead = {};
  if (edicao.candidato !== undefined && edicao.candidato !== base.candidato) saida.candidato = edicao.candidato;
  for (const f of FILTROS) {
    const kn = `${f}_nota` as const;
    const km = `${f}_motivo` as const;
    if (kn in edicao) {
      const nova = notaOuNula(edicao[kn]);
      if (nova !== base[f].nota) saida[kn] = nova;
    }
    if (km in edicao) {
      const novo = motivoLimpo(edicao[km]);
      if (novo !== motivoLimpo(base[f].motivo)) saida[km] = novo;
    }
  }
  return saida;
}

/** Frases de aviso antes de mandar (o servidor confere de novo). Vazio = pode gravar. */
export function errosDaLinha(campos: CamposBeachhead, limite: number): string[] {
  const erros: string[] = [];
  for (const f of FILTROS) {
    const nome = ROTULO_FILTRO[f].toLowerCase();
    const n = campos[`${f}_nota` as const];
    if (n != null && !ehNota(n)) erros.push(`A nota de ${nome} tem que ser 1, 2 ou 3.`);
    const m = campos[`${f}_motivo` as const];
    if (typeof m === 'string' && m.trim().length > limite) {
      erros.push(`O motivo de ${nome} passa de ${limite} letras (tem ${m.trim().length}).`);
    }
  }
  return erros;
}

/** Copia a sugestão da IA (nota e motivo) para a edição. Nota nula da IA só traz o motivo. */
export function aplicarSugestao(edicaoAtual: CamposBeachhead, linha: LinhaBeachhead, filtro: FiltroPessoa): CamposBeachhead {
  const s = linha.sugestao_ia?.[filtro];
  if (!s) return edicaoAtual;
  const novo: CamposBeachhead = { ...edicaoAtual };
  if (s.nota != null) novo[`${filtro}_nota` as const] = s.nota;
  if (s.motivo != null) novo[`${filtro}_motivo` as const] = s.motivo;
  return novo;
}

/** "2026-10-18T17:32:00Z" -> "18/10 às 14h32", no horário de São Paulo (não corta o texto UTC). */
export function textoQuando(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const partes = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const p = (t: string) => partes.find((x) => x.type === t)?.value ?? '';
  return `${p('day')}/${p('month')} às ${Number(p('hour'))}h${p('minute')}`;
}
